import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getListProjectExperience } from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import {
	ProjectExperienceCard,
	ProjectExperienceCardSkeleton,
} from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/ProjectExperience";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentCompanyId } from "hooks/auth";
import { LoadMoreCard } from "./LoadMoreCard";
import { AddNewCard } from "./AddNewCard";
import { AddNewProjectModal } from "./AddNewProjectModal";
import { ProjectExperienceTableView } from "./ProjectExperienceTableView";
import { RfxQuestion } from "newui/features/Rfx/types/returnable-schedule-question.type";
import type { ViewMode } from "./SmartFieldQuestionnaireBase";

interface ProjectExperienceItem {
	id: number;
	name?: string;
	[key: string]: unknown;
	client_name?: string;
	images?: string[];
	procurementMethodIds?: number[];
	value?: number;
	year_completed?: number;
}

interface FilterOption {
	label: string;
	value: number | string;
}

interface PaginationInfo {
	current_page: number;
	has_more_pages: boolean;
	total: number;
}

interface ProjectExperiencePageResponse {
	filters?: FilterOption[];
	included_items?: ProjectExperienceItem[];
	pagination?: PaginationInfo;
	project_experience?: ProjectExperienceItem[];
}

interface SmartFieldProjectExperienceProps {
	name: string;
	[key: string]: unknown;
	answer?: {
		selectedProjects?: ProjectExperienceItem[];
	};
	className?: string;
	disabled?: boolean;
	isClientPreview?: boolean;
	question: RfxQuestion;
	required?: boolean;
}

const projectDummyData: ProjectExperienceItem = {
	id: 0,
	name: "Example Project Card",
	value: 100,
	year_completed: new Date(Date.now()).getFullYear(),
	client_name: "Project Client",
	procurementMethodIds: [1],
};

export const SmartFieldProjectExperience: React.FC<SmartFieldProjectExperienceProps> = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState<FilterOption[]>([]);
	const [showAddModal, setShowAddModal] = useState(false);
	const [viewMode, setViewMode] = useState<ViewMode>("card");
	const companyId = useCurrentCompanyId();
	const {
		rfxId,
		hasRfxSubmissionLoaded,
		isClientView,
	} = useRfxSubmissionData();
	const { value, error, touched, isSelected, selectHandler, isSubmitting } =
		useSmartFieldSelection(props, { shouldSubmit: true });

	// Capture initial selections so selected items always appear (stable ref — won't trigger refetch)
	const [initialIncludeIds] = useState<number[]>(() =>
		Array.isArray(value) ? value : value ? [value] : []
	);

	const queryParams = {
		search: search,
		filters: selectedFilter,
		replacement: 1,
		rfq_id: rfxId,
		...(initialIncludeIds.length > 0 && { include_ids: initialIncludeIds }),
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedProjects?.length > 0) ||
		!rfxId ||
		isClientView;

	const {
		data,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isRefetching,
		refetch,
	} = useInfiniteQuery<ProjectExperiencePageResponse>({
		queryKey: ["projectExperience", queryParams],
		queryFn: (context) =>
			getListProjectExperience({
				queryKey: context.queryKey,
				pageParam: (context.pageParam as number) ?? 1,
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.pagination?.has_more_pages
				? lastPage.pagination.current_page + 1
				: undefined,
		enabled: hasRfxSubmissionLoaded && !useAnswerData && !isClientPreview,
	});

	// Flatten paginated results
	const allPages = data?.pages ?? [];
	const paginatedProjects = allPages.flatMap(
		(page) => page.project_experience ?? []
	);
	// Included items (selected items guaranteed by backend) — from first page only
	const includedItems = allPages[0]?.included_items ?? [];
	const filteringList = allPages[0]?.filters ?? [];

	// Merge paginated + included items (deduplicated)
	const mergedProjects = [...paginatedProjects];
	includedItems.forEach((item) => {
		if (!mergedProjects.some((p) => p.id === item.id)) {
			mergedProjects.push(item);
		}
	});

	const projectExperience =
		mergedProjects.length > 0
			? mergedProjects
			: (answer?.selectedProjects ?? []);

	// filter data for preview mode
	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		projectExperience,
		value,
		props?.disabled
	);

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
	const selectedData = projectExperience.filter((project) =>
		selectedIds.includes(project.id)
	);

	const pagination = allPages[allPages.length - 1]?.pagination;
	const remaining = pagination
		? pagination.total - paginatedProjects.length
		: 0;

	if (isClientPreview) {
		filteredData.push(projectDummyData);
	}

	const handleProjectCreated = (newId: number): void => {
		selectHandler(newId);
	};

	const showAddNew = !isClientPreview && !isClientView;
	const hasItems = mergedProjects.length > 0;

	return (
		<SmartFieldQuestionnaireBase
			{...props}
			className={className}
			question={question}
			required={required}
			description="Please choose the projects you would like to showcase."
			searchPlaceholder="Search project or client"
			isLoading={isLoading}
			loadingMessage="Loading your projects..."
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			type="project-experience"
			selectedData={selectedData}
			showFilter={!props?.disabled}
			showSearch={!props?.disabled}
			isClientPreview={isClientPreview}
			onAddNew={showAddNew && hasItems ? () => setShowAddModal(true) : undefined}
			addNewLabel={showAddNew && hasItems ? "Add Project" : undefined}
			onRefresh={showAddNew ? () => refetch() : undefined}
			isRefreshing={isRefetching}
			viewMode={viewMode}
			onViewModeChange={setViewMode}
			tableContent={
				filteredData.length > 0 ? (
					<>
						<ProjectExperienceTableView
							items={filteredData}
							selectedIds={selectedIds}
							onSelect={selectHandler}
							companyId={companyId ?? undefined}
							isPreview={props?.disabled}
							isSubmitting={isSubmitting}
						/>
						{hasNextPage && !props?.disabled && (
							<div className="mt-3 flex justify-center">
								<button
									type="button"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
									className="flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isFetchingNextPage ? "Loading..." : `Load More (${remaining})`}
								</button>
							</div>
						)}
					</>
				) : undefined
			}
			emptyStateContent={showAddNew && !hasItems && !isLoading ? (
				<div className="mt-4 w-full">
					<p className="mb-2 text-sm italic text-gray-500">
						Please add a new project to your profile, to submit it with your response. Once added here, it will be available for any future submissions.
					</p>
					<AddNewCard
						label="+ Add Project"
						onClick={() => setShowAddModal(true)}
						disabled={props.disabled}
					/>
				</div>
			) : undefined}
		>
			{isLoading ? (
				[...Array(3)].map((_, index) => (
					<ProjectExperienceCardSkeleton key={index} />
				))
			) : queryError ? (
				<div className="text-red-500">
					Error loading projects: {queryError.message || "Unknown error"}
				</div>
			) : filteredData.length === 0 && !showAddNew ? (
				<div className="italic text-gray-500">
					No projects {useAnswerData ? "selected" : "found"}
				</div>
			) : (
				<>
					{filteredData.map((project) => (
						<ProjectExperienceCard
							key={project.id}
							companyId={companyId}
							values={project}
							rfqSubmission={true}
							selected={isSelected(project?.id)}
							selectHandler={() => selectHandler(project?.id)}
							isClientPreview={isClientPreview}
							isPreview={props?.disabled}
							supplierSmartField
							isSubmitting={isSubmitting}
						/>
					))}
					{hasNextPage && !props?.disabled && (
						<LoadMoreCard
							onClick={() => fetchNextPage()}
							isLoading={isFetchingNextPage}
							remaining={remaining}
						/>
					)}
				</>
			)}
			{showAddModal && (
				<AddNewProjectModal
					isOpen={showAddModal}
					onClose={() => setShowAddModal(false)}
					onCreated={handleProjectCreated}
				/>
			)}
		</SmartFieldQuestionnaireBase>
	);
};
